// 匯出與證據（plan/vms-web-v0.md §5.8）：匯出工作列表（狀態 / 進度 / 下載證據包）、建立（≤ 4 路、每路 ≤ 1 小時、原因必填，
// 伺服器要 step-up）、分享連結（收件人、期限 / 密碼 / 次數、撤銷；連結只顯示這一次；期限不超過匯出包保存，platform#62）。
// 下載一律經 app-api 重驗權限的 proxy。全部分享連結的總表在「分享連結」分頁（SharesPage.tsx，EXP-10）。
// 進度：/v1/vms/stream 的 job 幀直接改列表（SPEC W24）；結束（成功 / 失敗）時重撈那個家戶拿檔案與簽章。
// 輪詢只在串流斷線時（進行中 2 秒、其他 15 秒）；重連成功由 LiveEventsProvider 重撈。
// 保存（SPEC #vms-web-export-archive）：免費 30 天、組織免費總量（頁首一行；用完〔新增匯出〕停用）、刪除匯出包（原因必填、step-up）。
import { DateTimeInput } from '../../components/DateTimeInput';
import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { KinioError, kinioFetchResponse } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useHomeLabel, useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { can, fullName } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { rangeStamp } from '../../lib/timefmt';
import { billingApi, exportApi, type BillingSubjectRef, type ExportCreateState, type ExportJob, type ExportQuota, type ShareLink } from '../../lib/planeApi';
import { ARCHIVE_PERIODS, archiveExpireAfter, archiveSizeClass, archiveSku, archiveTooLong, type ArchivePeriod } from './archive';
import { fmtBytes } from '../../lib/backup';
import { useLiveEvents } from '../../lib/liveEvents';
import type { JobFrame } from '@kinio/web-sdk';
import { EXPORT_SOURCES, blockReason, canExportFrom, failCode, sourcesOf, type ExportSourceKind } from './source';
import { PasswordInput } from '../../components/PasswordInput';
import { numParam } from '../../lib/links';
import { cloudSpans } from '../../lib/facade';
import { defaultSource, srcOf } from './source';
import { VerifyPage } from './VerifyPage';
import { defaultTtl, ttlOptions } from './share';

function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (e instanceof KinioError) {
    const b = blockReason(e.code, e.detail); // 組織沒開通 / worker 沒開 / 暫存不夠：同一組白話（SPEC #vms-web-export-prod）
    if (b) return t(`exports.block_${b}`);
  }
  if (e instanceof KinioError) return t(`exports.err_${e.code}`, { defaultValue: `${e.code}${e.message && e.message !== e.code ? ' · ' + e.message : ''}` });
  return String(e);
}

/** job 幀 → 列表的那一列（串流的 succeeded = 列表的 done）。 */
export function applyJobFrame(j: ExportJob, f: JobFrame): ExportJob {
  const status: ExportJob['status'] = f.state === 'succeeded' ? 'done' : f.state;
  return { ...j, status, progress: f.progress, ...(f.error ? { error: f.error } : {}) };
}

const MINUTE_OPTIONS = [1, 5, 10, 15, 30, 60];

/** 分享連結沒填次數時的上限（同伺服器預設）。 */
const SHARE_DEFAULT_MAX_VIEWS = 20;

function localInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function ExportsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const loc = useLocation();
  // 網址優先（/exports?new=1&cam=&from=&len=，P0-6）；舊入口的 router state 相容
  const [sp, setSp] = useSearchParams();
  const st = loc.state as { camera?: string; at?: number } | null;
  const urlNew = sp.get('new') === '1';
  const nav = urlNew
    ? { camera: sp.get('cam') ?? undefined, from: numParam(sp, 'from'), len: numParam(sp, 'len'), src: srcOf(sp.get('src')), reason: sp.get('reason') ?? undefined }
    : { camera: st?.camera, at: st?.at };
  const { visible } = useScope();
  const { tree, cameras: allCameras } = useDevices();
  // 匯出只列有 recording.export 的（「我的設備」的 actions）
  const cameras = useMemo(() => allCameras.filter((c) => can(c, 'recording.export')), [allCameras]);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { state: streamState, subscribeJob } = useLiveEvents();
  const streaming = streamState === 'open';
  // 各家戶現在能不能建新匯出（列表帶的 create；prod 要組織開通、worker 沒開或暫存不夠也不能建，SPEC #vms-web-export-prod）
  const [createState, setCreateState] = useState<Record<string, ExportCreateState>>({});
  // 各家戶所屬組織的免費保存用量（列表帶的 quota；同一個組織的家戶數字一樣）
  const [quotas, setQuotas] = useState<Record<string, ExportQuota>>({});
  const qs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['exports', h.home_id],
      queryFn: () =>
        exportApi.page(h.endpoints.api, h.home_id).then((p) => {
          setCreateState((s) =>
            s[h.home_id]?.allowed === p.create.allowed && s[h.home_id]?.reason === p.create.reason ? s : { ...s, [h.home_id]: p.create },
          );
          const q = p.quota;
          if (q)
            setQuotas((s) =>
              s[h.home_id]?.used_bytes === q.used_bytes && s[h.home_id]?.limit_bytes === q.limit_bytes ? s : { ...s, [h.home_id]: q },
            );
          return p.jobs;
        }),
      refetchInterval: (q: { state: { data?: ExportJob[] } }) =>
        streaming ? false : q.state.data?.some((j) => j.status === 'queued' || j.status === 'running') ? 2000 : 15000,
    })),
  });
  useEffect(
    () =>
      subscribeJob((f) => {
        if (f.kind !== 'export') return;
        const key = ['exports', f.homeId];
        const list = qc.getQueryData<ExportJob[]>(key);
        if (!list?.some((j) => j.job_id === f.jobId) || f.state === 'succeeded' || f.state === 'failed') {
          void qc.invalidateQueries({ queryKey: key }); // 新的工作 / 結束：重撈拿完整欄位（檔案、簽章、錯誤）
          if (!list) return;
        }
        qc.setQueryData<ExportJob[]>(key, (l) => l?.map((j) => (j.job_id === f.jobId ? applyJobFrame(j, f) : j)));
      }),
    [qc, subscribeJob],
  );
  const jobs = qs.flatMap((q, i) => (q.data ?? []).map((j) => ({ j, home: visible[i] }))).sort((a, b) => b.j.created_at.localeCompare(a.j.created_at));
  const [creating, setCreating] = useState(!!nav.camera || urlNew);
  // 有可匯出攝影機的家戶全都不能建 → 〔新增匯出〕停用，頁首一行說原因
  const exportHomes = [...new Set(cameras.map((c) => c.homeId))];
  const blocked = exportHomes.map((id) => createState[id]).filter((c): c is ExportCreateState => !!c && !c.allowed);
  const allBlocked = exportHomes.length > 0 && blocked.length === exportHomes.length;
  const [sharing, setSharing] = useState<{ j: ExportJob; api: string } | null>(null);
  const [deleting, setDeleting] = useState<{ j: ExportJob; api: string } | null>(null);
  const [archiving, setArchiving] = useState<{ j: ExportJob; subject: BillingSubjectRef } | null>(null);
  // 從 Stripe Checkout 回來（?archive=done&intent=&subj=org:<id>|home:<id>）：輪詢到 webhook 投影完成（同帳單頁）
  const [archiveNotice, setArchiveNotice] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const archiveRet = sp.get('archive');
  const archiveIntent = sp.get('intent');
  const archiveSubj = sp.get('subj');
  useEffect(() => {
    if (!archiveRet || !archiveIntent || !archiveSubj) return;
    if (archiveRet === 'cancel') {
      setArchiveNotice({ kind: 'warn', text: t('exports.archive_canceled') });
      setSp({}, { replace: true });
      return;
    }
    const [kind, id] = archiveSubj.split(':');
    const h = visible.find((x) => (kind === 'home' ? x.home_id === id : x.org_id === id));
    if (!h || (kind !== 'org' && kind !== 'home')) return;
    const subject: BillingSubjectRef = { api: h.endpoints.api, kind, id };
    let stop = false;
    setArchiveNotice({ kind: 'warn', text: t('exports.archive_processing') });
    const started = Date.now();
    const tick = async () => {
      if (stop) return;
      try {
        const r = await billingApi.checkoutStatus(subject, archiveIntent);
        if (r?.status === 'completed') {
          setArchiveNotice({ kind: 'ok', text: t('exports.archive_done') });
          setSp({}, { replace: true });
          void qc.invalidateQueries({ queryKey: ['exports'] });
          return;
        }
        if (r?.status === 'expired' || r?.status === 'failed') {
          setArchiveNotice({ kind: 'err', text: t('exports.archive_failed') });
          setSp({}, { replace: true });
          return;
        }
      } catch {
        /* 還沒投影完 / 範圍換了：等逾時 */
      }
      if (Date.now() - started > 120_000) {
        setArchiveNotice({ kind: 'warn', text: t('exports.archive_slow') });
        return;
      }
      setTimeout(tick, 1500);
    };
    void tick();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveRet, archiveIntent, archiveSubj, visible.length]);
  // 頁首的免費保存用量：每個組織一行（多個家戶同一個組織只列一次）
  const quotaRows = useMemo(() => {
    const seen = new Map<string, { q: ExportQuota; label: string }>();
    for (const id of exportHomes) {
      const q = quotas[id];
      const h = visible.find((x) => x.home_id === id);
      if (!q || !h) continue;
      const k = h.org_id ?? id;
      if (!seen.has(k)) seen.set(k, { q, label: h.display_name || id });
    }
    return [...seen.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotas, visible, exportHomes.join(',')]);
  const [err, setErr] = useState<string | null>(null);
  const camName = (homeId: string, ref: string) => {
    const c = cameras.find((x) => x.homeId === homeId && x.aid === ref);
    return c ? fullName(c, tree) : ref;
  };

  async function download(api: string, j: ExportJob) {
    setErr(null);
    try {
      const res = await kinioFetchResponse(exportApi.downloadUrl(api, j.home_id, j.job_id));
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${j.job_id}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
    } catch (e) {
      setErr(errText(t, e));
    }
  }

  return (
    <section>
      <div className="toolbar">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('exports.title')}
        </h1>
        <span className="spacer" />
        <button className="btn btn-primary" onClick={() => setCreating(true)} disabled={cameras.length === 0 || allBlocked}>
          {t('exports.new')}
        </button>
      </div>
      <ExportsTabs />
      <p className="muted small">{t('exports.help')}</p>
      {quotaRows.map(({ q, label }) => (
        <p key={label} className="muted small" data-testid="export-quota">
          {t('exports.quota_line', { used: fmtBytes(q.used_bytes), limit: fmtBytes(q.limit_bytes) })}
          {quotaRows.length > 1 ? ` · ${label}` : ''}
        </p>
      ))}
      {allBlocked && (
        <p className="notice small" data-testid="export-blocked">
          {t(`exports.block_${blocked[0].reason ?? 'gateway_unreachable'}`)}
        </p>
      )}
      {err && <p className="error">{err}</p>}
      {archiveNotice && (
        <p className={`card ${archiveNotice.kind === 'err' ? 'error' : ''}`} role="status" data-testid="archive-notice">
          {archiveNotice.text}
        </p>
      )}
      <ul className="export-list">
        {jobs.map(({ j, home }) => (
          <li key={j.job_id} className="card export-row" data-job={j.job_id} data-status={j.status}>
            <div className="export-main">
              <div>
                <strong>{j.sources.map((s) => camName(j.home_id, s.device_ref)).join(t('common.list_sep'))}</strong>
                <span className={`badge exp-${j.status}`} style={{ marginLeft: 8 }}>
                  {t(`exports.st_${j.status}`)}
                  {j.status === 'running' ? ` ${j.progress}%` : ''}
                </span>
                {j.status === 'done' && <span className="badge">{j.signed ? t('exports.signed') : t('exports.unsigned')}</span>}
                {j.status === 'done' && j.retention === 'archive' && (
                  <span className="badge badge-ok" data-testid="export-archived">
                    {t('exports.archived')}
                  </span>
                )}
                {sourcesOf(j.sources)
                  .filter((k) => k !== 'cloud')
                  .map((k) => (
                    <span key={k} className="badge" data-testid="export-source">
                      {t(`exports.src_${k}`)}
                    </span>
                  ))}
              </div>
              <div className="muted small">
                {j.sources.map((s) => rangeStamp(s.from, s.to, tz)).join(t('common.list_sep2'))}
              </div>
              <div className="muted small">
                {t('exports.reason')}
                {t('common.colon')}
                {j.reason} · {home.display_name || home.home_id} · {formatDateTime(Date.parse(j.created_at) / 1000, tz, i18n.language)}
              </div>
              {j.status === 'done' && j.expire_at && (
                <div className="muted small" data-testid="export-retention">
                  {t(j.retention === 'archive' ? 'exports.kept_archive' : 'exports.kept_free', { at: formatDateTime(Date.parse(j.expire_at) / 1000, tz, i18n.language) })}
                  {j.size_bytes ? ` · ${fmtBytes(j.size_bytes)}` : ''}
                </div>
              )}
              {j.status === 'deleted' && j.delete_reason && (
                <div className="muted small">
                  {t('exports.deleted_reason')}
                  {t('common.colon')}
                  {j.delete_reason}
                </div>
              )}
              {j.error && (
                <div className="error small" data-testid="export-error" title={j.error}>
                  {failCode(j.error) ? t(`exports.fail_${failCode(j.error)}`) : j.error}
                </div>
              )}
              {j.status !== 'done' && j.status !== 'failed' && j.status !== 'expired' && sourcesOf(j.sources).includes('local') && (
                <div className="muted small">{t('exports.local_waiting')}</div>
              )}
              {j.status === 'running' && (
                <div className="ai-cap">
                  <div className="ai-cap-bar" style={{ width: `${j.progress}%` }} />
                </div>
              )}
            </div>
            {j.status === 'done' && (
              <div className="export-actions">
                <button className="btn btn-sm" onClick={() => void download(home.endpoints.api, j)}>
                  {t('exports.download')}
                </button>
                <button className="btn btn-sm" onClick={() => setSharing({ j, api: home.endpoints.api })}>
                  {t('exports.share')}
                </button>
                {archiveSizeClass(j.size_bytes ?? 0) && (
                  <button
                    className="btn btn-sm"
                    data-testid="export-archive"
                    onClick={() =>
                      setArchiving({
                        j,
                        subject:
                          home.org_kind === 'team' && home.org_id
                            ? { api: home.endpoints.api, kind: 'org', id: home.org_id }
                            : { api: home.endpoints.api, kind: 'home', id: home.home_id },
                      })
                    }
                  >
                    {t(j.retention === 'archive' ? 'exports.archive_extend' : 'exports.archive')}
                  </button>
                )}
                {(j.retention !== 'archive' || home.can_manage || home.role === 'owner' || home.role === 'admin') && (
                  <button className="btn btn-sm btn-danger" data-testid="export-delete" onClick={() => setDeleting({ j, api: home.endpoints.api })}>
                    {t('exports.delete')}
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
        {jobs.length === 0 && !qs.some((q) => q.isLoading) && <li className="muted">{t('exports.none')}</li>}
      </ul>
      {creating && !allBlocked && (
        <CreateDialog
          createState={createState}
          initialCamera={nav.camera}
          initialAt={'at' in nav ? nav.at : undefined}
          initialFrom={'from' in nav ? nav.from : undefined}
          initialLen={'len' in nav ? nav.len : undefined}
          initialSource={'src' in nav ? nav.src : undefined}
          initialReason={'reason' in nav ? nav.reason : undefined}
          close={() => {
            setCreating(false);
            if (urlNew) setSp({}, { replace: true }); // 關掉後重新整理不再自動打開
          }}
          done={(homeId) => {
            setCreating(false);
            if (urlNew) setSp({}, { replace: true });
            void qc.invalidateQueries({ queryKey: ['exports', homeId] });
          }}
        />
      )}
      {sharing && <ShareDialog job={sharing.j} api={sharing.api} close={() => setSharing(null)} />}
      {archiving && <ArchiveDialog job={archiving.j} subject={archiving.subject} close={() => setArchiving(null)} />}
      {deleting && (
        <DeleteDialog
          job={deleting.j}
          api={deleting.api}
          close={() => setDeleting(null)}
          done={() => {
            const homeId = deleting.j.home_id;
            setDeleting(null);
            void qc.invalidateQueries({ queryKey: ['exports', homeId] });
          }}
        />
      )}
    </section>
  );
}

/**
 * 長期保管（延長）：期間三選一（顯示這個包的級距、價格、延長後到期）、原因必填、「提前刪除不退費」→〔前往付款〕轉 Stripe Checkout。
 * 價格讀付款主體的帳單總覽（export_archive 商品）；讀不到（沒有看帳單的權限）就只寫級距，付款頁會顯示金額。
 */
function ArchiveDialog({ job, subject, close }: { job: ExportJob; subject: BillingSubjectRef; close(): void }) {
  const { t, i18n } = useTranslation();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [opened] = useState(() => Date.now());
  const [period, setPeriod] = useState<ArchivePeriod>('1y');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const size = archiveSizeClass(job.size_bytes ?? 0);
  const ov = useQuery({ queryKey: ['billing', subject.kind, subject.id], queryFn: () => billingApi.overview(subject), retry: false });
  const priceOf = (p: ArchivePeriod) => {
    const prod = size ? ov.data?.products.find((x) => x.kind === 'export_archive' && x.sku === archiveSku(size, p)) : undefined;
    if (!prod) return null;
    try {
      return new Intl.NumberFormat(i18n.language, { style: 'currency', currency: prod.currency.toUpperCase() }).format(prod.unit_amount / 100);
    } catch {
      return `${(prod.unit_amount / 100).toFixed(2)} ${prod.currency.toUpperCase()}`;
    }
  };
  const after = archiveExpireAfter(job.expire_at, period, opened);
  const tooLong = archiveTooLong(after, opened);
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const u = new URL(window.location.href);
      u.search = '';
      u.searchParams.set('subj', `${subject.kind}:${subject.id}`);
      const r = await exportApi.archive(subject.api, job.home_id, job.job_id, { period, reason: reason.trim(), return_url: u.toString() });
      if (r?.url) {
        window.location.assign(r.url); // 回來時網址帶 archive=done&intent=…
        return;
      }
    } catch (e) {
      setErr(errText(t, e));
    }
    setBusy(false);
  }
  return (
    <Dialog open title={t(job.retention === 'archive' ? 'exports.archive_extend_title' : 'exports.archive_title')} onClose={close}>
      <div className="form">
        <p className="small">{t('exports.archive_help', { size: fmtBytes(job.size_bytes ?? 0), cls: size ? t(`exports.archive_size_${size}`) : '' })}</p>
        <fieldset className="field" data-testid="archive-periods">
          <span>{t('exports.archive_period')}</span>
          {ARCHIVE_PERIODS.map((p) => (
            <label key={p} className="check">
              <input type="radio" name="archive-period" checked={period === p} onChange={() => setPeriod(p)} data-testid={`archive-period-${p}`} />
              {t(`exports.archive_p_${p}`)}
              {priceOf(p) && <span className="muted small"> · {priceOf(p)}</span>}
            </label>
          ))}
        </fieldset>
        <p className="muted small" data-testid="archive-after">
          {t('exports.archive_after', { at: formatDateTime(after / 1000, tz, i18n.language) })}
        </p>
        {tooLong && <p className="error small">{t('exports.err_ARCHIVE_TOO_LONG')}</p>}
        <label className="field">
          <span>{t('exports.archive_reason')}</span>
          <input className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} data-testid="archive-reason" />
        </label>
        <p className="notice small">{t('exports.archive_no_refund')}</p>
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <button className="btn btn-primary" disabled={busy || tooLong || reason.trim().length < 4} onClick={() => void submit()} data-testid="archive-pay">
            {t('exports.archive_pay')}
          </button>
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** 刪除匯出包：原因必填（4 字以上，寫稽核）；長期保管中的另寫「已付的費用不退」（SPEC #vms-web-export-archive）。 */
function DeleteDialog({ job, api, close, done }: { job: ExportJob; api: string; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await exportApi.remove(api, job.home_id, job.job_id, reason.trim());
      done();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('exports.delete_title')} onClose={close}>
      <div className="form">
        <p className="small">{t('exports.delete_help')}</p>
        {job.retention === 'archive' && (
          <p className="notice small" data-testid="export-delete-norefund">
            {t('exports.delete_no_refund')}
          </p>
        )}
        <label className="field">
          <span>{t('exports.delete_reason')}</span>
          <input className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} data-testid="export-delete-reason" />
        </label>
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <button className="btn btn-danger" disabled={busy || reason.trim().length < 4} onClick={() => void submit()} data-testid="export-delete-confirm">
            {t('exports.delete_confirm')}
          </button>
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function CreateDialog({
  createState,
  initialCamera,
  initialAt,
  initialFrom,
  initialLen,
  initialSource,
  initialReason,
  close,
  done,
}: {
  /** 各家戶能不能建（列表的 create）；選到不能建的家戶時說原因、停用〔建立匯出〕 */
  createState: Record<string, ExportCreateState>;
  /** 網址 src=（cloud / local / backup）：指定來源；沒帶 = 用這段時間實際有錄影的來源（SPEC #vms-web-540） */
  initialSource?: ExportSourceKind;
  /** 網址 reason=：預填原因 */
  initialReason?: string;
  initialCamera?: string;
  initialAt?: number;
  /** 網址帶的起點（unix 秒）與長度（秒）；優先於 initialAt（舊入口：事件時間前 5 分鐘起） */
  initialFrom?: number;
  initialLen?: number;
  close(): void;
  done(homeId: string): void;
}) {
  const { t } = useTranslation();
  const { visible } = useScope();
  const homeLabel = useHomeLabel();
  const { tree, cameras: allCameras } = useDevices();
  const cameras = useMemo(() => allCameras.filter((c) => can(c, 'recording.export')), [allCameras]);
  const first = cameras.find((c) => c.key === initialCamera) ?? cameras[0];
  const [homeId, setHomeId] = useState(first?.homeId ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set(first ? [first.aid] : []));
  const at = (initialAt ?? Date.now() / 1000 - 600) * 1000;
  const [from, setFrom] = useState(localInput(initialFrom !== undefined ? initialFrom * 1000 : at - 5 * 60_000));
  const [minutes, setMinutes] = useState(() => {
    if (initialLen === undefined) return 10;
    const want = initialLen / 60;
    return MINUTE_OPTIONS.reduce((a, b) => (Math.abs(b - want) < Math.abs(a - want) ? b : a));
  });
  const [reason, setReason] = useState(initialReason ?? '');
  const [source, setSourceRaw] = useState<ExportSourceKind>(initialSource ?? 'cloud');
  // 使用者自己選過來源（或網址指定）就不再自動換
  const [sourcePicked, setSourcePicked] = useState(!!initialSource);
  const setSource = (k: ExportSourceKind) => {
    setSourcePicked(true);
    setSourceRaw(k);
  };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const homeCams = useMemo(() => cameras.filter((c) => c.homeId === homeId), [cameras, homeId]);
  const home = visible.find((h) => h.home_id === homeId);
  const homeBlocked = createState[homeId]?.allowed === false ? (createState[homeId].reason ?? 'gateway_unreachable') : null;
  // 直接用網址開（新分頁）時設備還沒載入：載好後再選上網址指定的攝影機
  useEffect(() => {
    if (homeId || !first) return;
    setHomeId(first.homeId);
    setPicked(new Set([first.aid]));
  }, [homeId, first]);
  useEffect(() => {
    if (!homeCams.length) return; // 設備還沒載入：不要把網址預選的攝影機清掉
    setPicked((s) => new Set([...s].filter((aid) => homeCams.some((c) => c.aid === aid) && canExportFrom(source, aid))));
  }, [homeCams, source]);

  // 這段時間雲端有沒有錄影（跟回放時間軸同一份資料，façade recordings?source=cloud）：
  // 來源預設 = 實際有錄影的來源（雲端有就雲端，否則 NVR / Kinio Station 硬碟），選了沒錄影的來源直接說
  const firstCam = homeCams.find((c) => picked.has(c.aid));
  const fromMs = new Date(from).getTime();
  const cloudHas = useQuery({
    queryKey: ['export-cloud-has', firstCam?.key, fromMs, minutes],
    enabled: !!firstCam && Number.isFinite(fromMs),
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        const spans = await cloudSpans(firstCam!.apiEndpoint, firstCam!.homeId, firstCam!.aid, fromMs / 1000, fromMs / 1000 + minutes * 60);
        return spans.length > 0;
      } catch {
        return false;
      }
    },
  });
  useEffect(() => {
    if (sourcePicked || !firstCam || cloudHas.data === undefined) return;
    setSourceRaw(defaultSource(cloudHas.data, firstCam.aid));
    setSourcePicked(true); // 只自動選一次，之後換攝影機 / 時間不再改使用者看到的來源
  }, [sourcePicked, firstCam, cloudHas.data]);

  async function submit() {
    if (!home) return;
    setBusy(true);
    setErr(null);
    const f = new Date(from);
    const to = new Date(f.getTime() + minutes * 60_000);
    try {
      await exportApi.create(home.endpoints.api, home.home_id, {
        reason,
        sources: [...picked].map((device_ref) => ({ device_ref, from: f.toISOString(), to: to.toISOString(), ...(source === 'cloud' ? {} : { source }) })),
      });
      done(home.home_id);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open title={t('exports.new')} onClose={close} wide>
      <div className="form">
        {visible.length > 1 && (
          <label className="field">
            <span>{t('exports.home')}</span>
            <select className="input" value={homeId} onChange={(e) => setHomeId(e.target.value)}>
              {visible.map((h) => (
                <option key={h.home_id} value={h.home_id}>
                  {homeLabel(h)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('exports.source')}</span>
          <select className="input" value={source} onChange={(e) => setSource(e.target.value as ExportSourceKind)}>
            {EXPORT_SOURCES.map((k) => (
              <option key={k} value={k}>
                {t(`exports.src_${k}`)}
              </option>
            ))}
          </select>
        </label>
        <p className="muted small">{t(`exports.src_${source}_help`)}</p>
        {source === 'cloud' && cloudHas.data === false && firstCam && (
          <p className="notice small" data-testid="export-no-rec">
            {t('exports.no_rec_in_source', { src: t('exports.src_cloud') })}
          </p>
        )}
        <fieldset className="field">
          <legend>{t('exports.cameras', { max: 4 })}</legend>
          <div className="check-grid">
            {homeCams.map((c) => (
              <label key={c.key} className="check">
                <input
                  type="checkbox"
                  checked={picked.has(c.aid)}
                  disabled={!canExportFrom(source, c.aid) || (!picked.has(c.aid) && picked.size >= 4)}
                  onChange={() =>
                    setPicked((s) => {
                      const n = new Set(s);
                      if (n.has(c.aid)) n.delete(c.aid);
                      else n.add(c.aid);
                      return n;
                    })
                  }
                />
                {fullName(c, tree)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="toolbar">
          <label className="field">
            <span>{t('exports.from')}</span>
            <DateTimeInput value={from} onChange={setFrom} />
          </label>
          <label className="field">
            <span>{t('exports.minutes')}</span>
            <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {t('exports.n_min', { n: m })}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t('exports.reason')}</span>
          <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('exports.reason_ph')} />
        </label>
        {homeBlocked && (
          <p className="notice small" data-testid="export-home-blocked">
            {t(`exports.block_${homeBlocked}`)}
          </p>
        )}
        {err && <p className="error">{err}</p>}
        <div className="toolbar">
          <button
            className="btn btn-primary"
            disabled={busy || !!homeBlocked || picked.size === 0 || reason.trim().length < 4}
            onClick={() => void submit()}
          >
            {t('exports.create')}
          </button>
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function ShareDialog({ job, api, close }: { job: ExportJob; api: string; close(): void }) {
  const { t, i18n } = useTranslation();
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  // 期限不超過匯出包保存（platform#62）：只列短於剩餘時間的選項，最後一個是「到匯出包到期」
  const [opened] = useState(() => Date.now());
  const ttls = useMemo(() => ttlOptions(job.expire_at, opened), [job.expire_at, opened]);
  const [ttl, setTtl] = useState(() => defaultTtl(ttls));
  const [recipient, setRecipient] = useState('');
  const [clampedAt, setClampedAt] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  // 分享連結的密碼是這裡新設的（對方要靠它開）→ 輸入兩次（SPEC UX-21）
  const [password2, setPassword2] = useState('');
  const pwMismatch = password2 !== '' && password !== password2;
  // 觀看次數預設 20（沒填伺服器也是 20；新連結不能無上限，SPEC #vms-web-export-prod）
  const [maxViews, setMaxViews] = useState(String(SHARE_DEFAULT_MAX_VIEWS));
  const [created, setCreated] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const load = () =>
    exportApi
      .shareLinks(api, job.home_id, job.job_id)
      .then(setLinks)
      .catch((e) => setErr(errText(t, e)));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.job_id]);

  async function create() {
    if (password !== password2) {
      setErr(t('activate.mismatch'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await exportApi.share(api, job.home_id, job.job_id, {
        expires_in_seconds: ttl,
        recipient: recipient.trim(),
        ...(password ? { password } : {}),
        ...(maxViews ? { max_views: Number(maxViews) } : {}),
      });
      if (r) setCreated(`${location.origin}/share/${r.token}`);
      setClampedAt(r?.clamped ? r.link.expires_at : null);
      setPassword('');
      setPassword2('');
      setRecipient('');
      await load();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open title={t('exports.share_title')} onClose={close} wide>
      <div className="form">
        {job.expire_at && (
          <p className="muted small" data-testid="share-export-expiry">
            {t('exports.share_export_expiry', { at: formatDateTime(Date.parse(job.expire_at) / 1000, tz, i18n.language) })}
          </p>
        )}
        {ttls.length === 0 && (
          <p className="notice small" data-testid="share-expiring">
            {t('exports.err_EXPORT_EXPIRES_SOON')}
          </p>
        )}
        <label className="field">
          <span>{t('exports.recipient')}</span>
          <input
            className="input"
            value={recipient}
            maxLength={100}
            required
            onChange={(e) => setRecipient(e.target.value)}
            placeholder={t('exports.recipient_ph')}
            aria-describedby="share-recipient-help"
          />
          <span id="share-recipient-help" className="muted small">
            {t('exports.recipient_help')}
          </span>
        </label>
        <div className="toolbar">
          <label className="field">
            <span>{t('exports.ttl')}</span>
            <select className="input" value={ttl} onChange={(e) => setTtl(Number(e.target.value))} disabled={ttls.length === 0}>
              {ttls.map((o) => (
                <option key={o.untilExport ? 'until' : o.seconds} value={o.seconds}>
                  {o.untilExport
                    ? t('exports.ttl_until_export', { at: formatDateTime(Date.parse(job.expire_at!) / 1000, tz, i18n.language) })
                    : t(`exports.ttl_${o.seconds}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('exports.max_views')}</span>
            <input
              className="input"
              type="number"
              min={1}
              max={10000}
              value={maxViews}
              onChange={(e) => setMaxViews(e.target.value)}
              placeholder={String(SHARE_DEFAULT_MAX_VIEWS)}
              aria-describedby="share-max-views-help"
              data-testid="share-max-views"
            />
            <span id="share-max-views-help" className="muted small">
              {t('exports.max_views_help', { n: SHARE_DEFAULT_MAX_VIEWS })}
            </span>
          </label>
          <label className="field">
            <span>{t('exports.share_password')}</span>
            <PasswordInput
              className="input"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('exports.optional')}
            />
          </label>
          <label className="field">
            <span>{t('exports.share_password2')}</span>
            <PasswordInput
              className="input"
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              aria-invalid={pwMismatch || undefined}
            />
          </label>
        </div>
        <span className="error small" role="status" data-pw-mismatch>
          {pwMismatch ? t('activate.mismatch') : ''}
        </span>
        <button className="btn btn-primary" disabled={busy || !recipient.trim() || ttls.length === 0} onClick={() => void create()}>
          {t('exports.share_create')}
        </button>
        {created && (
          <div className="notice" data-share-url={created}>
            <p className="small">{t('exports.share_once')}</p>
            <input className="input mono" readOnly value={created} onFocus={(e) => e.currentTarget.select()} />
            {clampedAt && (
              <p className="small" data-testid="share-clamped">
                {t('exports.share_clamped', { at: formatDateTime(Date.parse(clampedAt) / 1000, tz, i18n.language) })}
              </p>
            )}
          </div>
        )}
        {err && <p className="error">{err}</p>}
        <ul className="ai-list">
          {(links ?? []).map((l) => {
            const gone = !!l.revoked_at || Date.parse(l.expires_at) < Date.now();
            return (
              <li key={l.link_id} data-link={l.link_id}>
                <span className="small">{l.recipient || t('exports.no_recipient')}</span>
                <span className="mono small" title={l.link_id}>
                  {l.code ?? l.link_id}
                </span>
                <span className="muted small">
                  {t('exports.expires', { at: formatDateTime(Date.parse(l.expires_at) / 1000, tz, i18n.language) })} ·{' '}
                  {t('exports.views', { n: l.views, max: l.max_views ?? '∞' })}
                  {l.has_password ? ` · ${t('exports.has_password')}` : ''}
                </span>
                {gone ? (
                  <span className="badge">{l.revoked_at ? t('exports.revoked') : t('exports.expired')}</span>
                ) : (
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      void exportApi
                        .revoke(api, job.home_id, job.job_id, l.link_id)
                        .then(load)
                        .catch((e) => setErr(errText(t, e)))
                    }
                  >
                    {t('exports.revoke')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
}

/** 匯出與證據的頁內分頁：匯出工作｜分享連結｜驗證證據包（SPEC #vms-web-220 選單表、#vms-web-540）。 */
export function ExportsTabs() {
  const { t } = useTranslation();
  return (
    <div className="tabs tabs-links" role="tablist" data-testid="exports-tabs">
      <NavLink to="/exports" end role="tab">
        {t('exports.tab_jobs')}
      </NavLink>
      <NavLink to="/exports/shares" role="tab">
        {t('shares.tab')}
      </NavLink>
      <NavLink to="/exports/verify" role="tab">
        {t('verify.tab')}
      </NavLink>
    </div>
  );
}

/** 殼層內的「驗證證據包」分頁（/exports/verify；B-20）。 */
export function ExportsVerifyPage() {
  const { t } = useTranslation();
  return (
    <section>
      <h1 className="page-title">{t('exports.title')}</h1>
      <ExportsTabs />
      <VerifyPage embedded />
    </section>
  );
}
